/**
 * How a change set's category and principals are NAMED on screen — pure, plus one hook.
 *
 * **Category.** `ChangeSetSummary.category` is the platform's identifier (`P1`…`P7`), the seven
 * categories `17` §1 names (bata-platform `planning/principles/changeset.py`, `CATEGORIES`). It was
 * handed to `Badge` as a catalogue key, so every card read `⟦P7⟧` (production, v0.37.0). Each is now
 * a key of its own, registered in `tools/i18n-key-coverage.mjs`'s dynamic families; a category this
 * build does not know reads as such rather than as a marker.
 *
 * **Principal.** `author` and `approvedBy` carry the verified principal's SUBJECT — for a human
 * signed in through the realm, a UUID. Since bata-platform#508 (usability audit B6, DEC-111) the
 * reads publish the person's profile name beside it (`authorName`, `approvedByNames` aligned index
 * by index, `closedByName`, `recordedByName`), and that name is shown. The platform's own principal
 * reads «Sistema» (`@/planning`'s `personLabel`). Without a name a subject is shown as before: the
 * caller's own as «you», a UUID as a labelled short account reference, anything else (a service
 * account's name) as recorded. Never an invented name.
 *
 * Since bata-platform 0.84 (ADR 2127) every name has an `…AbsentReason` beside it: where it says
 * `machine`, `service_account` or `no_profile` the reason is said in words («Servizio automatico») and
 * the short account reference is not used; the screen puts the id behind the «id» button
 * (`PersonIdTag`).
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useAccessGrants } from '@/auth/grants';
import { personText } from '@/planning';

/** `17` §1's seven categories, in the platform's order. */
export const CHANGE_CATEGORIES = ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7'] as const;
export type ChangeCategory = (typeof CHANGE_CATEGORIES)[number];

const CATEGORY_KEY: Readonly<Record<ChangeCategory, string>> = {
  P1: 'foundations.changeCategory.P1',
  P2: 'foundations.changeCategory.P2',
  P3: 'foundations.changeCategory.P3',
  P4: 'foundations.changeCategory.P4',
  P5: 'foundations.changeCategory.P5',
  P6: 'foundations.changeCategory.P6',
  P7: 'foundations.changeCategory.P7',
};

/** The catalogue key naming a category — `foundations.changeCategory.unknown` for one not listed. */
export function categoryLabelKey(category: string): string {
  return (CATEGORY_KEY as Readonly<Record<string, string>>)[category] ?? 'foundations.changeCategory.unknown';
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** How a principal is shown, before translation. */
export type PrincipalLabel =
  | { readonly kind: 'self' }
  | { readonly kind: 'account'; readonly short: string }
  | { readonly kind: 'verbatim'; readonly name: string };

/** A recorded principal, classified: the caller, an opaque realm subject, or a readable name. */
export function principalLabel(principal: string, self: string | null): PrincipalLabel {
  if (self !== null && principal === self) return { kind: 'self' };
  if (UUID.test(principal)) return { kind: 'account', short: principal.slice(0, 8) };
  return { kind: 'verbatim', name: principal };
}

/**
 * A function naming a recorded principal in the reader's language: «Sistema» for the platform, the
 * profile name the read publishes beside the subject, else the subject as before (`principalLabel`).
 */
export function usePrincipalName(): (principal: string, name?: string | null, reason?: string | null) => string {
  const { t } = useTranslation();
  const { subject } = useAccessGrants();
  // Stable across renders, so a table's memoised columns are not rebuilt on every one.
  return useCallback((principal: string, name: string | null = null, reason: string | null = null) => personText(t, principal, name, (id) => {
    const label = principalLabel(id, subject);
    if (label.kind === 'self') return t('foundations.principal.you');
    if (label.kind === 'account') return t('foundations.principal.account', { short: label.short });
    return label.name;
  }, reason), [t, subject]);
}

/**
 * Approvers by name, aligned index by index with their subjects (`approvedByNames`) and the reasons
 * a name is absent (`approvedByNamesAbsentReasons`).
 */
export function principalNames(
  name: (principal: string, name?: string | null, reason?: string | null) => string,
  subjects: readonly string[] | null | undefined,
  names: readonly (string | null)[] | null | undefined,
  reasons?: readonly (string | null)[] | null,
): string[] {
  return (subjects ?? []).map((subject, index) => name(subject, names?.[index] ?? null, reasons?.[index] ?? null));
}
